import { getUserId } from 'App/services/idpTokenService';
import { REWARD_PROVIDER_ENUMS } from 'Pages/rewards/constants';
import { REDRedeemStatsResponse } from 'Shared/models/swagger/red';
import { APIResponse, MS_API } from 'Shared/services/apiInterface';
import { getIdempotencyKeyCookie } from 'Shared/services/red/app/redService';
import * as route from 'Shared/services/red/routes';
import { SwrKeys } from 'Shared/services/swr/swrKeys';
import getRedemptionMSDevice from 'Shared/utils/deviceUtils';
import useSWR, { SWRResponse } from 'swr';

interface RedemptionPayload {
  userPaymentMethodId: number;
  points             : number;
  device             : string;
  redemptionId      ?: string;
}

interface PostPaymentMethodsPayload {
  rewardProvider    : string;
  authorizationCode?: string;
  email            ?: string;
  programId        ?: number;
  sku              ?: string;
  idempotencyKey   ?: string;
  firstName        ?: string;
  lastName         ?: string;
  routingNumber    ?: string;
  accountNumber    ?: string;
  bankAccountType  ?: string;
  bankNickname     ?: string;
}

/**
 * Returns redemption Microservice Endpoint URL
 * @param routeType
 */
const getRedUrl = (urlType: string): string => {
  const userId = getUserId();

  switch (urlType) {
    case route.RED_REDEEM:
      return `${route.API_CONFIG_RED_MS}/${userId}${route.RED_REDEEM}`;
    case route.RED_PAYMENT_METHODS:
      return `${route.API_CONFIG_RED_MS}/${userId}${route.RED_PAYMENT_METHODS}`;
    case route.RED_REDEMPTION_STATUS:
      return `${route.API_CONFIG_RED_MS}/${userId}${route.RED_REDEMPTION_STATUS}`;
    case route.REDEEM_EMAIL_CODE_MS:
      return `${route.API_CONFIG_RED_MS}/${userId}${route.REDEEM_EMAIL_CODE_MS}`;
    case route.REDEEM_VERIFY_CODE_MS:
      return `${route.API_CONFIG_RED_MS}/${userId}${route.REDEEM_VERIFY_CODE_MS}`;
    case route.RED_USER_STATS:
      return `${route.API_CONFIG_RED_MS}/${userId}${route.RED_USER_STATS}`;
  }

  return '';
};

/**
 * Retrieves user stats from RED MS
 * @param dateFrom ISO date string
 * @param dateTo ISO date string
 * @param isLoggedIn
 * @returns {Promise<APIResponse>}
 */
const getUserStats = async (dateFrom: string, dateTo: string, isLoggedIn = true): Promise<REDRedeemStatsResponse> => {
  if (!isLoggedIn) {
    return { pointsRedeemed: 0, pointsEarned: 0 };
  }

  const baseURL = process.env.REACT_APP_RED_BASE_URL;
  const url     = getRedUrl(route.RED_USER_STATS);

  try {
    const response = await MS_API({
      method: 'GET',
      baseURL: baseURL,
      url: url,
      params: {
        dateFrom: dateFrom,
        dateTo  : dateTo
      }
    });
    return response.data;
  } catch {
    return { pointsRedeemed: 0, pointsEarned: 0 };
  }
};


/**
 * POST PayPal auth code to RED MS
 * @param authCode - code returned from Paypal login redirect
 */
const postPaypalAuthCode = (authCode: string): Promise<APIResponse> => {
  const baseURL        = process.env.REACT_APP_RED_BASE_URL;
  const url            = getRedUrl(route.RED_PAYMENT_METHODS);
  const idempotencyKey = getIdempotencyKeyCookie();
  const data: PostPaymentMethodsPayload = {
    rewardProvider   : REWARD_PROVIDER_ENUMS.paypal,
    authorizationCode: authCode
  };

  if (idempotencyKey !== '') {
    data.idempotencyKey = idempotencyKey;
  }

  return MS_API({
    method : 'POST',
    baseURL: baseURL,
    url    : url,
    data   : data
  });
};

/**
 * POST redemption to RED MS
 * @param points
 * @param formFactor
 * @param paymentId
 */
const postRedemptionAPI = (
  points       : number,
  paymentId    : number,
  redemptionId : string
): Promise<APIResponse> => {
  const baseURL    = process.env.REACT_APP_RED_BASE_URL;
  const url        = getRedUrl(route.RED_REDEEM);
  const deviceType = getRedemptionMSDevice();

  const data: RedemptionPayload = {
    userPaymentMethodId: paymentId,
    points             : points,
    device             : deviceType
  };

  /**
   * If redemptionId is provided, we need to pass it to the RED MS
   * (Its passed when redemption verification is enabled and 2fa is completed successfully)
   */
  if (redemptionId !== '') {
    data.redemptionId = redemptionId;
  }

  return MS_API({
    method : 'POST',
    baseURL: baseURL,
    url    : url,
    data   : data
  });
};

/**
 * POST email/giftcard for user to RED MS
 * @param email
 * @param programId - id for particular giftcard
 * @param sku - sku for particular giftcard
 */
const postGiftcardEmail = (email: string, programId: number, sku: string): Promise<APIResponse> => {
  const userId  = getUserId();
  const idempotencyKey = getIdempotencyKeyCookie();
  const data: PostPaymentMethodsPayload = {
    rewardProvider: REWARD_PROVIDER_ENUMS.giftcard,
    email         : email,
    programId     : programId,
    sku           : sku
  };

  if (idempotencyKey !== '') {
    data.idempotencyKey = idempotencyKey;
  }

  return MS_API({
    method : 'POST',
    baseURL: process.env.REACT_APP_RED_BASE_URL,
    url    : `${route.API_CONFIG_RED_MS}/${userId}${route.RED_PAYMENT_METHODS}`,
    data   : data
  });
};

/**
 * POST all bank information to RED MS
 * @param firstName
 * @param lastName
 * @param email
 * @param routingNumber
 * @param accountNumber
 * @param accountType
 * @param bankNickname
 */
export const postAllBankInfo = (
  firstName    : string,
  lastName     : string,
  email        : string,
  routingNumber: string,
  accountNumber: string,
  accountType  : string,
  bankNickname : string
): Promise<APIResponse> => {
  const url = getRedUrl(route.RED_PAYMENT_METHODS);
  const idempotencyKey = getIdempotencyKeyCookie();
  const data: PostPaymentMethodsPayload = {
    email          : email,
    firstName      : firstName,
    lastName       : lastName,
    routingNumber  : routingNumber,
    accountNumber  : accountNumber,
    bankAccountType: accountType,
    bankNickname   : bankNickname,
    rewardProvider : REWARD_PROVIDER_ENUMS.bankTransfer
  };

  if (idempotencyKey !== '') {
    data.idempotencyKey = idempotencyKey;
  }

  return MS_API({
    method : 'POST',
    baseURL: process.env.REACT_APP_RED_BASE_URL,
    url    : url,
    data   : data
  });
};

/**
 * GET Payment Methods
 */
export const getPaymentOptions = (): Promise<APIResponse> => {
  const baseURL = process.env.REACT_APP_RED_BASE_URL;
  const userId  = getUserId();

  return MS_API({
    method : 'GET',
    baseURL: baseURL,
    url    : `${route.API_CONFIG_RED_MS}/${userId}${route.RED_REDEMPTION_OPTIONS}`,
  });
};

/**
 * GET User Payment Methods from RED MS
 */
export const getUserPaymentMethods = (): Promise<APIResponse> => {
  const url = getRedUrl(route.RED_PAYMENT_METHODS);

  return MS_API({
    method : 'GET',
    baseURL: process.env.REACT_APP_RED_BASE_URL,
    url    : url,
  });
};

/**
 * @returns {SWRResponse<any, any>}
 */
export const useGetUserPaymentMethods = (): SWRResponse<any, any> => {
  return useSWR(SwrKeys.PaymentMethods, getUserPaymentMethods);
};

/**
 * @returns {SWRResponse<any, any>}
 */
export const useGetUserStats = (dateFrom: string, dateTo: string, isLoggedIn = true): SWRResponse<REDRedeemStatsResponse, any> => {
  return useSWR(SwrKeys.UserStats, () => getUserStats(dateFrom, dateTo, isLoggedIn));
};

/**
 * POST request to RED MS to email verification code to the user
 * to user's email address.
 * @returns
 */
const emailVerificationCode = (): Promise<APIResponse> => {
  return MS_API({
    method : 'POST',
    baseURL: process.env.REACT_APP_RED_BASE_URL,
    url    : getRedUrl(route.REDEEM_EMAIL_CODE_MS)
  });
};

/**
 * Post request to RED MS to validate code user entered.
 * @param code
 */
const verifyCode = (code: string): Promise<APIResponse> => {
  return MS_API({
    method : 'POST',
    baseURL: process.env.REACT_APP_RED_BASE_URL,
    url    : getRedUrl(route.REDEEM_VERIFY_CODE_MS),
    data   : {
      code: code
    }
  });
};

export {
  emailVerificationCode,
  getRedUrl,
  getUserStats,
  postGiftcardEmail,
  postPaypalAuthCode,
  postRedemptionAPI,
  verifyCode
};
